(() => {
  if (window.NNCartTrackingInitialized) return;
  window.NNCartTrackingInitialized = true;

  const APP_URL = "https://free-gift-price-override.onrender.com";
  const SCRIPT = document.currentScript;
  const SHOP_DOMAIN = SCRIPT?.dataset.shop || window.NNCartTrackingShop || window.Shopify?.shop || window.location.hostname;
  const STORAGE_KEY = "nn_cart_tracking_session_id";
  const GA_EVENT_MAP = {
    cart_opened: "nn_cart_drawer_open",
    checkout_clicked: "nn_cart_checkout_click",
    upsell_viewed: "nn_cart_upsell_view",
    upsell_added: "nn_cart_upsell_add",
    free_gift_added: "nn_cart_free_gift_add",
    discount_applied: "nn_cart_discount_apply",
    discount_error: "nn_cart_discount_error"
  };

  const getSessionId = () => {
    try {
      let id = localStorage.getItem(STORAGE_KEY);
      if (!id) {
        id = crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        localStorage.setItem(STORAGE_KEY, id);
      }
      return id;
    } catch {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  };

  const parseMoneyCents = (text) => {
    const normalized = String(text || "").replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", ".");
    const value = Number.parseFloat(normalized);
    return Number.isFinite(value) ? Math.round(value * 100) : 0;
  };

  const moneyFromCents = (cents) => Math.round((Number(cents) || 0)) / 100;

  const pushAnalyticsEvent = (event) => {
    const gaEventName = GA_EVENT_MAP[event.event_type];
    if (!gaEventName) return;

    const sessionId = getSessionId();
    const item = event.product_title || event.variant_id || event.product_id ? {
      item_id: event.product_id || event.variant_id || undefined,
      item_name: event.product_title || undefined,
      item_variant: event.variant_id || undefined,
      item_list_name: event.module || event.source || "Cart Drawer",
      price: moneyFromCents(event.price_cents),
      quantity: Number(event.quantity) || 1,
    } : undefined;
    const ecommerce = item ? {
      currency: event.currency || window.Shopify?.currency?.active || "EUR",
      value: moneyFromCents((Number(event.price_cents) || 0) * (Number(event.quantity) || 1)),
      items: [item]
    } : undefined;
    const payload = {
      event: gaEventName,
      nn_event_type: event.event_type,
      cart_session_id: sessionId,
      cart_token: event.cart_token || undefined,
      module: event.module || undefined,
      source: event.source || undefined,
      discount_code: event.discount_code || undefined,
      path: window.location.pathname,
      ecommerce
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    if (typeof window.gtag === "function" && !window.google_tag_manager) {
      window.gtag("event", gaEventName, {
        cart_session_id: sessionId,
        cart_token: event.cart_token || undefined,
        module: event.module || undefined,
        source: event.source || undefined,
        discount_code: event.discount_code || undefined,
        currency: ecommerce?.currency,
        value: ecommerce?.value,
        items: ecommerce?.items
      });
    }

    document.dispatchEvent(new CustomEvent("nn:cart-tracking:event", { detail: payload }));
  };

  const sendEvent = (event) => {
    pushAnalyticsEvent(event);

    const payload = JSON.stringify({
      ...event,
      shop: SHOP_DOMAIN,
      cart_session_id: getSessionId(),
      pathname: window.location.pathname,
      occurred_at: new Date().toISOString()
    });
    const url = `${APP_URL}/api/cart-events?shop=${encodeURIComponent(SHOP_DOMAIN)}`;

    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true
    }).catch(() => {});
  };

  const getProductDataFromCard = (card) => {
    const title = card.dataset.productTitle || card.querySelector(".nn-cart-upsell-card__title")?.textContent?.trim() || "";
    const priceText = card.querySelector("sale-price")?.textContent || card.querySelector(".nn-cart-upsell-card__price")?.textContent || "";
    const isGift = card.classList.contains("nn-cart-upsell-card--gift");

    return {
      event_type: "upsell_added",
      module: isGift ? "Free Gift" : "Upsells",
      product_title: title,
      product_id: card.dataset.productId || "",
      variant_id: card.querySelector('input[name="id"]')?.value || "",
      quantity: 1,
      price_cents: parseMoneyCents(priceText),
      currency: window.Shopify?.currency?.active || "EUR",
      source: card.querySelector('input[name="properties[_Quelle]"]')?.value || "Cart Drawer"
    };
  };

  const viewedUpsells = new WeakSet();
  const observedUpsells = new WeakSet();
  const trackedDiscountCodes = new Set();
  let cartOpenTracked = false;

  const observeUpsells = (root = document) => {
    if (!("IntersectionObserver" in window)) return;

    if (!window.NNCartUpsellObserver) {
      window.NNCartUpsellObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || viewedUpsells.has(entry.target)) return;
          viewedUpsells.add(entry.target);
          const data = getProductDataFromCard(entry.target);
          sendEvent({ ...data, event_type: "upsell_viewed" });
        });
      }, { threshold: 0.55 });
    }

    root.querySelectorAll?.(".nn-cart-upsell-card").forEach((card) => {
      if (observedUpsells.has(card)) return;
      observedUpsells.add(card);
      window.NNCartUpsellObserver.observe(card);
    });
  };

  const trackCartOpen = () => {
    if (cartOpenTracked) return;
    cartOpenTracked = true;
    sendEvent({ event_type: "cart_opened", module: "Cart Drawer" });
    requestAnimationFrame(() => observeUpsells(document));
    window.setTimeout(() => {
      cartOpenTracked = false;
    }, 1500);
  };

  const isCartDrawerOpen = () => {
    const drawer = document.querySelector("#cart-drawer, cart-drawer, .cart-drawer");
    return Boolean(drawer?.hasAttribute("open"));
  };

  document.addEventListener("click", (event) => {
    const cartTrigger = event.target.closest('[aria-controls="cart-drawer"], [href$="/cart"], [href*="/cart?"]');
    if (cartTrigger) {
      window.setTimeout(() => {
        if (isCartDrawerOpen()) trackCartOpen();
      }, 120);
    }

    const checkoutButton = event.target.closest('button[name="checkout"], a[href*="/checkout"]');
    if (checkoutButton?.closest("#cart-drawer, cart-drawer, .cart-drawer")) {
      sendEvent({
        event_type: "checkout_clicked",
        module: "Cart Drawer",
        price_cents: parseMoneyCents(checkoutButton.textContent || "")
      });
    }

  }, { capture: true });

  document.addEventListener("variant:add", (event) => {
    const upsellCard = event.target?.closest?.(".nn-cart-upsell-card");
    if (!upsellCard) return;

    const data = getProductDataFromCard(upsellCard);
    const addedItem = event.detail?.items?.[0];
    const isGift = upsellCard.classList.contains("nn-cart-upsell-card--gift");

    sendEvent({
      ...data,
      event_type: isGift ? "free_gift_added" : "upsell_added",
      product_id: addedItem?.product_id || data.product_id,
      variant_id: addedItem?.variant_id || data.variant_id,
      quantity: addedItem?.quantity || data.quantity
    });
  });

  document.addEventListener("cart:discount:error", (event) => {
    sendEvent({
      event_type: "discount_error",
      module: "Discount Code",
      source: event.detail?.type || "invalid"
    });
  });

  document.addEventListener("cart:change", (event) => {
    const cart = event.detail?.cart;
    const discountCodes = (cart?.discount_codes || []).filter((discount) => discount.applicable !== false).map((discount) => discount.code).filter(Boolean);
    discountCodes.forEach((discountCode) => {
      if (trackedDiscountCodes.has(discountCode)) return;

      trackedDiscountCodes.add(discountCode);
      sendEvent({
        event_type: "discount_applied",
        module: "Discount Code",
        discount_code: discountCode,
        cart_token: cart?.token || "",
        price_cents: cart?.total_price || 0,
        currency: cart?.currency || window.Shopify?.currency?.active || "EUR"
      });
    });
    requestAnimationFrame(() => observeUpsells(document));
  });

  document.addEventListener("dialog:after-show", (event) => {
    if (event.target?.matches?.("#cart-drawer, cart-drawer, .cart-drawer")) {
      trackCartOpen();
    }
  });

  const boot = () => {
    observeUpsells(document);
    const drawer = document.querySelector("#cart-drawer, cart-drawer, .cart-drawer");
    if (isCartDrawerOpen()) {
      trackCartOpen();
    }

    if (drawer && "MutationObserver" in window) {
      new MutationObserver(() => {
        if (isCartDrawerOpen()) trackCartOpen();
        observeUpsells(drawer);
      }).observe(drawer, { attributes: true, childList: true, subtree: true, attributeFilter: ["open", "class"] });
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();
